import { useState } from "react";
import { IDataResult, ResponseStatus } from "../../common/models/Result";
import { useForm } from "../../common/viewmodels/DefaultVM";
import { DataUtils } from "../../common/views/DataUtils";
import { Utils } from "../../common/views/Utils";
import { KeyValue, OptionModel } from "../../home/models/OtherTypes";
import { SortModel } from "../../home/models/PageTypes";
import {
    ShortCustomerModel, CustomerFilterType, CustomerFilterModel, CustomerLevel
} from "../models/CustomerModel";
import { CustomerAdminService } from "../services/CustomerService";

export function useCustomerAdminVM() {
    const customerService = new CustomerAdminService();

    const [customers, setCustomers] = useState<ShortCustomerModel[]>([]);
    const [counts, setCounts] = useState<KeyValue<CustomerFilterType, number>[]>([]);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [types, setTypes] = useState<OptionModel[]>([]);

    const [pageHash, setPageHash] = useState<number>(0);
    const [listHash, setListHash] = useState<number>(0);
    const [loading, setLoading] = useState(false);

    const {
        form: filter,
        setForm: setFilter,
        resetForm: resetFilter,
        setField: setFilterField
    } = useForm<CustomerFilterModel>({
        filterType: CustomerFilterType.all,
        level: CustomerLevel.none,
        grade: 0,
        search: "",
        pageNumber: 1,
        pageSize: 50,
        sortModel: new SortModel(),
        pagingDisabled: false
    });

    const onLoad = () => {
        setTypes(getTypeOptions());
        getList();
        getCounts();
    }

    const getList = (getCount = true, pageChange = false) => {
        setLoading(true);
        setCustomers([]);
        setListHash(Utils.getRandomKey());

        if (!filter.filterType) filter.filterType = CustomerFilterType.all;
        if (!filter.level) filter.level = CustomerLevel.none;

        const request = customerService.search(filter);

        DataUtils.onRequest(
            request,
            (result: IDataResult) => onSearchResult(result, pageChange)
        );

        if (getCount) getCounts();
    }

    const onSearchResult = (result: IDataResult, pageChange = false) => {
        if (result.status === ResponseStatus.success && result.data) {
            const customers = result.data.items as ShortCustomerModel[];
            const totalCount = result.data.totalCount;

            setCustomers(customers);
            setTotalCount(totalCount);
            setListHash(Utils.getRandomKey());

            if (!pageChange) setPageHash(Utils.getRandomKey() + 100);
        }

        setLoading(false);
    }

    const getCounts = () => {
        setCounts([]);
        const request = customerService.getCountByTypes(filter);
        DataUtils.onRequest(request, onGetCountsResult);
    }

    const onGetCountsResult = (result: IDataResult) => {
        var counts = result.data as KeyValue<string, number>[];

        if (counts) {
            const types = getTypeOptions();

            for (let i = 0; i < types.length; i++) {
                const option = types[i];
                const type = option.key;
                const count = counts.find((item: KeyValue<string, number>) =>
                    item.key === type);

                option.count = count ? count.value : 0;
            }

            setTypes(types);
        }
    }

    const getTypeOptions = () => {
        const employeeOption = new OptionModel();
        employeeOption.key = CustomerFilterType.employeeOnly;
        employeeOption.text = "Employees Only";
        employeeOption.current = false;
        employeeOption.count = 0;

        const userOption = new OptionModel();
        userOption.key = CustomerFilterType.userOnly;
        userOption.text = "Users Only";
        userOption.current = false;
        userOption.count = 0;

        const newInvoiceOption = new OptionModel();
        newInvoiceOption.key = CustomerFilterType.invoiceAvailable;
        newInvoiceOption.text = "New Invoices";
        newInvoiceOption.current = false;
        newInvoiceOption.count = 0;

        const openSaleOption = new OptionModel();
        openSaleOption.key = CustomerFilterType.openSaleOnly;
        openSaleOption.text = "Open Sales";
        openSaleOption.current = false;
        openSaleOption.count = 0;

        const wholeSaleOption = new OptionModel();
        wholeSaleOption.key = CustomerFilterType.wholesaleOnly;
        wholeSaleOption.text = "Wholesale Only";
        wholeSaleOption.current = false;
        wholeSaleOption.count = 0;

        const allOption = new OptionModel();
        allOption.key = CustomerFilterType.all;
        allOption.text = "All";
        allOption.current = true;
        allOption.count = 0;

        return [
            employeeOption,
            userOption,
            newInvoiceOption,
            openSaleOption,
            wholeSaleOption,
            allOption
        ];
    }

    return {
        customers,
        counts,
        totalCount,

        filter,
        setFilter,
        resetFilter,
        setFilterField,

        loading,
        setLoading,

        onLoad,
        getList,
        getCounts,

        types,
        setTypes,

        pageHash,
        listHash
    }
}